/**
 * Manual library order, as fractional ranks.
 *
 * A rank is a string compared lexicographically. Between any two ranks there is
 * always another, so moving one document writes one document — not the whole
 * list, which would turn every drag into a sync storm. Base 36, and a rank never
 * ends in `0`: `a` and `a0` sort as neighbours with nothing between them.
 */

const DIGITS = '0123456789abcdefghijklmnopqrstuvwxyz';
const BASE = DIGITS.length;

function digit(s: string, i: number, fallback: number): number {
  if (i >= s.length) return fallback;
  return DIGITS.indexOf(s[i] ?? '0');
}

/**
 * A rank strictly between `before` and `after`. `null` means "no bound": the
 * start or the end of the list.
 */
export function rankBetween(before: string | null, after: string | null): string {
  const a = before ?? '';
  if (after !== null && a >= after) {
    throw new Error(`rankBetween: ${JSON.stringify(a)} is not before ${JSON.stringify(after)}`);
  }
  let prefix = '';
  for (let i = 0; ; i++) {
    const lo = digit(a, i, 0);
    const hi = after === null ? BASE : digit(after, i, 0);
    if (lo === hi) {
      prefix += DIGITS[lo];
      continue;
    }
    const mid = Math.floor((lo + hi) / 2);
    if (mid > lo) return prefix + DIGITS[mid];
    // Adjacent digits: keep `before`'s digit and find room after the rest of it.
    return prefix + DIGITS[lo] + rankBetween(a.slice(i + 1), null);
  }
}

/** `count` evenly spread ranks, for ordering a list that had none. */
export function spreadRanks(count: number): string[] {
  const ranks: string[] = [];
  let previous: string | null = null;
  for (let i = 0; i < count; i++) {
    // Two digits give 1295 slots; step so the list fills about half of them and
    // leaves room at both ends.
    const width = count < 600 ? 2 : 3;
    const slots = BASE ** width;
    const value = Math.round(((i + 1) * slots) / (count * 2 + 1)) + Math.floor(slots / 4);
    let rank = value.toString(BASE).padStart(width, '0').replace(/0+$/, '');
    if (previous !== null && rank <= previous) rank = rankBetween(previous, null);
    ranks.push(rank);
    previous = rank;
  }
  return ranks;
}

export interface Ranked {
  id: string;
  rank?: string | null;
}

/**
 * The rank updates that move `movedId` to position `toIndex` of `group` (the
 * list as displayed, which may or may not contain the moved document).
 *
 * One write when the neighbours already have ranks. A group with unranked
 * members — every group, the first time — is ranked as a whole, in its
 * displayed order, so the first drag never reshuffles what the user was looking at.
 */
export function planMove(group: Ranked[], movedId: string, toIndex: number): Ranked[] {
  const others = group.filter((d) => d.id !== movedId);
  const index = Math.max(0, Math.min(toIndex, others.length));
  const ordered = [...others.slice(0, index), { id: movedId }, ...others.slice(index)];

  const ranksValid = others.every(
    (d, i) =>
      typeof d.rank === 'string' &&
      d.rank !== '' &&
      (i === 0 || (others[i - 1]?.rank ?? '') < d.rank),
  );
  if (ranksValid) {
    const before = others[index - 1]?.rank ?? null;
    const after = others[index]?.rank ?? null;
    return [{ id: movedId, rank: rankBetween(before, after) }];
  }
  const ranks = spreadRanks(ordered.length);
  const current = new Map(group.map((d) => [d.id, d.rank ?? null]));
  return ordered
    .map((d, i) => ({ id: d.id, rank: ranks[i] ?? null }))
    .filter((d) => d.id === movedId || current.get(d.id) !== d.rank);
}

/** Order for the manual sort: unranked (new) documents first, newest first; then by rank. */
export function compareManual(
  a: { rank?: string | null; createdAt: number },
  b: { rank?: string | null; createdAt: number },
): number {
  const ra = a.rank ?? null;
  const rb = b.rank ?? null;
  if (ra === null && rb === null) return b.createdAt - a.createdAt;
  if (ra === null) return -1;
  if (rb === null) return 1;
  return ra < rb ? -1 : ra > rb ? 1 : 0;
}
